import React from "react";
import styled, { css, keyframes } from "styled-components";

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 9999;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Header = styled.div`
  position: absolute;
  top: 20px;
  left: 0;
  width: 100%;
  padding: 0 16px;
  z-index: 10;
  display: flex;
  gap: 4px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 40px;
  right: 16px;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  z-index: 20;
  cursor: pointer;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

const progressAnimation = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const ProgressBarContainer = styled.div`
  height: 2px;
  background-color: rgba(255, 255, 255, 0.3);
  flex: 1;
  border-radius: 2px;
  overflow: hidden;
`;

interface BarProps {
  active: boolean;
  completed: boolean;
  duration: number;
}

const Bar = styled.div<BarProps>`
  height: 100%;
  background-color: white;
  width: ${props => (props.completed ? "100%" : "0%")};
  
  ${props =>
    props.active &&
    css`
      animation: ${progressAnimation} ${props.duration}s linear forwards;
    `}
`;

const TouchArea = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  z-index: 5;
`;

const RightTouchArea = styled(TouchArea)`
  right: 0;
`;

const LeftTouchArea = styled(TouchArea)`
  left: 0;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%);
  z-index: 5;
  pointer-events: none;
`;

interface RecapStoryProps {
  currentSlide: number;
  totalSlides: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  duration?: number;
  children: React.ReactNode;
}

const RecapStory: React.FC<RecapStoryProps> = ({
  currentSlide,
  totalSlides,
  onNext,
  onPrev,
  onClose,
  duration = 10,
  children
}) => {
  return (
    <Container>
      <GradientOverlay />
      <Header>
        {Array.from({ length: totalSlides }).map((_, idx) => (
          <ProgressBarContainer key={idx}>
            <Bar
              active={idx === currentSlide}
              completed={idx < currentSlide}
              duration={duration}
              onAnimationEnd={() => {
                if (idx === currentSlide) {
                  onNext();
                }
              }}
            />
          </ProgressBarContainer>
        ))}
      </Header>

      <CloseButton onClick={onClose}>&times;</CloseButton>

      <ContentContainer>
        <LeftTouchArea onClick={onPrev} />
        {children}
        <RightTouchArea onClick={onNext} />
      </ContentContainer>
    </Container>
  );
};

export default RecapStory;
